import {
  ENTITY_TYPES,
  GRID_LIST_VIEW_TYPE,
  IAuditTrailData,
  IAuditTrailFormRule,
  IBaseEntityType,
  IEntityConfig,
  IEntityFilterItem,
  IInventoryCountGroup,
  IInventoryCountItemItemViewAuditLog,
  IFilterQuery,
  IDocumentOperationOptions,
  IApiListResponse,
  IAuditLog,
  IAuditLogItem,
  IInventoryCountItemUpdatedFieldsMap,
  IInventoryCountMeasureAuditLogItem,
  IInventoryCountItem,
  IInventoryCountItemCount,
  IMajorCategory,
} from '../../types';
import { Injectable, Injector } from '@angular/core';
import { BaseService } from '../base';
import {
  flow,
  forEach,
  get,
  head,
  isEmpty,
  isEqual,
  isNil,
  map,
  range,
  reverse,
  set,
  sortBy,
  toLower,
  chain,
  find,
  keyBy,
  keys,
  uniq,
  isNull,
  unset,
} from 'lodash';
import {
  MAX_PAGE_SIZE,
  TIME_FORMATS,
  DEFAULT_LOCALE,
} from '@config/global-constants';
import { generateObjectId, toMoment } from '@shared/utils';
import { PdfDataStructure } from '@app/shared/common/make-pdf/pdf-data-structure.type';
import { INVENTORY_COUNT_GROUP_PDF_HEADERS } from '@app/shared/types/enums/inventory-count-pdf.enum';
import { COMMON_FORM_PATH } from '@app/shared/constants';
import { getAuditTrailTemp } from './audit-trail-form-rules';
import { BehaviorSubject, Observable, rx } from '@app/shared/rx';
import { HttpParams } from '@angular/common/http';
import { returnOperatorItem } from '@app/shared/utils/audit-trail-common-utils';
import { environment } from 'environments/environment';
import { NumberFormatService } from '../number-format';
import { lazyInject } from '@containers/base/decorators';
import { ITreeData } from '@app/shared/types/common/tree.type';
import { InventoryCountUpdatedItem } from './inventory-count.type';
import { I18n } from '@app/i18n/strings-keys';
import { decimalNumber } from '@app/shared/common';

const entityType: ENTITY_TYPES = ENTITY_TYPES.INVENTORY_COUNT;
const config: IEntityConfig = {
  ENTITY_TYPE: entityType,
  isMasterListAvailable: true,
  apiEndpoint: entityType,
  uiRoute: entityType,
  isFormServiceSupported: true,
  needConvertTimeZoneFields: ['start_date', 'date'],
  auditTrailUrl: {
    item: 'inventory-count-item',
  },
  list: {},
  buttons: {},
};

/**
 * Inventory Count service that extends BaseService to use
 * basic CRUD request operations
 */
@Injectable()
export class InventoryCountService extends BaseService {
  public static config: IEntityConfig = config;
  public static ENTITY_TYPE: string = config.ENTITY_TYPE;

  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;

  public tableSortConfig: any[];
  public isSortChangeFromGrid: boolean = false;

  public viewSetting: GRID_LIST_VIEW_TYPE = null;
  public auditTrailList: IAuditTrailData[] = [];
  public itemList: IInventoryCountItem[];
  public auditTrailTitle: string;
  public auditTrailSubTitle: string;
  public initialGridItems: ITreeData[] = [];
  public reallyChangedInventoryItems: ITreeData[] = [];

  public updatedFieldsMap: IInventoryCountItemUpdatedFieldsMap = {
    'inventory-count-item': ['counts'],
  };

  public constructor(public injector: Injector) {
    super(injector);
  }

  public backToFrontItemMapper(doc: IBaseEntityType): IBaseEntityType {
    set(
      doc,
      'virtual.inventory_count_items',
      get(doc, 'inventory_count_items'),
    );
    delete doc.inventory_count_items;

    return flow(super.backToFrontItemMapper.bind(this))(doc);
  }

  /**
   * @deprecated it not used now, we should use pdf service v2 in child class
   * need remove this method
   */
  public transformDocumentToPdfStructure(
    currentDocument: IBaseEntityType,
  ): PdfDataStructure {
    const createDate = this.companyService.convertUTCDateWithTimeZone(
      toMoment().toISOString(),
      true,
      TIME_FORMATS.L_LT,
    );
    const pdfStructure: PdfDataStructure = {
      createDate,
      pageTitle: 'Inventory Count Sheet',
      equalColumnWidth: true,
      id: get(currentDocument, 'id', ''),
      tableData: [
        {
          headers: [...map(INVENTORY_COUNT_GROUP_PDF_HEADERS, 'headerText')],
          bodyData: [],
        },
      ],
    };

    if (!isEmpty(currentDocument?.inventory_count_items)) {
      const tableData = [];
      let sortedItem = map(currentDocument.inventory_count_items, (item) => ({
        ...item,
        sortIdentity: `${toLower(get(item, COMMON_FORM_PATH.reportingCategoryMajorName, ''))}_${toLower(
          get(item, COMMON_FORM_PATH.reportingCategoryMinorName, ''),
        )}_${toLower(get(item, 'name', ''))}`,
      }));
      if (!isEmpty(this.tableSortConfig)) {
        const sortConfig = head(this.tableSortConfig);
        sortedItem = sortBy(
          currentDocument.inventory_count_items,
          'sortIdentity',
        );

        if (sortConfig.sort === 'desc' && this.isSortChangeFromGrid) {
          sortedItem = reverse(sortedItem);
        }
      }
      forEach(sortedItem, (iInventoryCountItem: any) => {
        const tbodyRow = [
          iInventoryCountItem.name,
          iInventoryCountItem.reporting_category_major.name,
        ];
        const counts = range(1, 4);
        forEach(counts, (val: number, i: number) => {
          if (!isEmpty(iInventoryCountItem.measures[i])) {
            let description;
            if (!isEmpty(iInventoryCountItem.measures[i].measure_description)) {
              description = iInventoryCountItem.measures[i].measure_description;
            }
            if (!isEmpty(iInventoryCountItem.measures[i].description)) {
              description = iInventoryCountItem.measures[i].description;
            }
            tbodyRow.push(description ?? '');
            tbodyRow.push(String(iInventoryCountItem.measures[i].count ?? '0'));
          } else {
            tbodyRow.push('');
            tbodyRow.push('');
          }
        });
        tableData.push(tbodyRow);
      });
      pdfStructure.tableData[0].bodyData.push({
        tableTitle: '',
        data: tableData,
      });
    }

    return pdfStructure;
  }

  public frontToBackItemMapper(
    doc: IInventoryCountGroup,
  ): IInventoryCountGroup {
    const newDoc = Object.assign({}, doc);

    const currentDocument = this.stateService.getByPath([
      'entities',
      ENTITY_TYPES.INVENTORY_COUNT,
      'currentDocument',
    ]);
    const updatedItems: InventoryCountUpdatedItem[] = map(
      this.reallyChangedInventoryItems,
      (item) => {
        const reportingCategoryMajor: IMajorCategory = {
          entity_id: item?.reporting_category_major?.entity_id,
          name: item?.reporting_category_major?.name,
          i18n: item?.reporting_category_major?.i18n || {},
        };
        const reportingCategoryMinor: IMajorCategory = {
          entity_id: item?.reporting_category_minor?.entity_id,
          name: item?.reporting_category_minor?.name,
          i18n: item?.reporting_category_minor?.i18n || {},
        };
        const counts = map(
          this.isCategoryListView() ? item?.children : item.measures,
          (count) => {
            return {
              measure_description: count.description,
              measure_entity_id: count.entity_id,
              count: isNil(count.count)
                ? null
                : decimalNumber(count.count).valueOf(),
              factor_value: count.factor_value,
            };
          },
        );

        return {
          entity_id: item?.entity_id || generateObjectId(),
          site_id: item?.site_id || doc?.site_id,
          inventory_count_entity_id:
            currentDocument?.entity_id || doc?.entity_id,
          inventory_item_entity_id: item?.entity_id,
          name: item?.name || '',
          description: item?.description || '',
          count: item?.manager_count || 0,
          system_count: item?.system_count || 0,
          variance_count: item?.variance_count || 0,
          cost: item?.inventory_item_cost || 0,
          variance_cost: item?.variance_cost || 0,
          counts: counts || [],
          reporting_category_major: reportingCategoryMajor,
          reporting_category_minor: reportingCategoryMinor,
          i18n: item?.i18n || {},
        };
      },
    );

    set(newDoc, 'inventory_count_items', updatedItems);
    this.initialGridItems = [];
    this.reallyChangedInventoryItems = [];

    return super.frontToBackItemMapper(newDoc);
  }

  public isCategoryListView(): boolean {
    // When the user does not configure list view or CATEGORY_VIEW, it will return false.
    return isEqual(this.viewSetting, GRID_LIST_VIEW_TYPE.CATEGORY_VIEW);
  }

  /**
   * return two type template form-rules for audit trail
   * information // item
   */
  public getAuditTrailTemp(
    data: IInventoryCountItemItemViewAuditLog[],
    options: {
      type: 'item';
      loader$: BehaviorSubject<boolean>;
      user$: BehaviorSubject<IEntityFilterItem[]>;
    },
  ): IAuditTrailFormRule {
    return getAuditTrailTemp(this, data, options);
  }

  public getInventoryCountItemList(
    query?: IFilterQuery,
    options: IDocumentOperationOptions = {},
  ): Observable<IInventoryCountItem[]> {
    const params: HttpParams = this.createParams({
      ...query,
      top: MAX_PAGE_SIZE,
      orderby: 'updated_at desc',
    });
    const headers = this.composeHeadersForUpdateRequest({
      ...options,
    });

    return this.http
      .get<IApiListResponse>(`/inventory-count-item/`, { params, headers })
      .pipe(
        this.catchHttpError(options.errorOverride),
        rx.map((response) => {
          if (Array.isArray(response.items)) {
            return response;
          }
          throw new Error(response.errorMessage);
        }),
        rx.map((response: IApiListResponse<IInventoryCountItem>) =>
          get(response, 'items', []),
        ),
      );
  }

  public applyAuditLogsWithPopulatedLogs(
    populatedLogs: IAuditLog[],
  ): IInventoryCountItemItemViewAuditLog[] {
    const result = [];
    const sortedLogs = chain(populatedLogs)
      .flatten()
      .orderBy('date', 'desc')
      .value();

    sortedLogs.forEach((doc: IAuditLog) => {
      const records = this.generateRecords(doc);

      result.push(...records);
    });

    return result;
  }

  public hasChangedCount(
    previousCount: IInventoryCountItemCount,
    endCount: IInventoryCountItemCount,
  ): boolean {
    return !isEqual(previousCount, endCount);
  }

  public generateRecords(
    doc: IAuditLog,
  ): IInventoryCountItemItemViewAuditLog[] {
    const date = doc.date;
    const operation = find(this.operateList, {
      operator_id: doc.info.person_id,
    });

    const previousCountsMap = keyBy(
      get(doc, `info.previousFields.counts`, null),
      'measure_entity_id',
    );
    const endCountsMap = keyBy(
      get(doc, `info.updatedFields.counts`, null),
      'measure_entity_id',
    );

    const measureEntityIds = uniq([
      ...keys(previousCountsMap),
      ...keys(endCountsMap),
    ]);

    let records = [];
    measureEntityIds.forEach((measureEntityId: string) => {
      const previousCount = previousCountsMap[measureEntityId];
      const endCount = endCountsMap[measureEntityId];
      const { userLocale: locale } = this.stateService.getDictionaryLocals();
      if (
        (!isNil(previousCount?.count) || !isNil(endCount?.count)) &&
        !isEqual(previousCount, endCount)
      ) {
        const processI18nForCount = (item: IInventoryCountItemCount) => {
          if (isEmpty(item?.i18n?.[locale])) {
            unset(item, 'i18n');
          } else {
            item.i18n = { [locale]: item.i18n[locale] };
          }

          return item;
        };

        const previousCountWithI18n = processI18nForCount(previousCount);
        const endCountWithI18n = processI18nForCount(endCount);
        const hasChanged = this.hasChangedCount(
          previousCountWithI18n,
          endCountWithI18n,
        );
        if (!hasChanged) {
          // If no change, skip to generate the record
          return;
        }
        /**
         * Handle the locale for measure description
         */
        const setLocalizedDescription = (data: IInventoryCountItemCount) => {
          if (!isEmpty(get(data, `i18n.${locale}.measure_description`))) {
            data.measure_description = data.i18n[locale].measure_description;
          } else if (data?.measure_description) {
            data.measure_description = `${data.measure_description} [${DEFAULT_LOCALE}]`;
          }
        };
        if (locale !== DEFAULT_LOCALE) {
          setLocalizedDescription(previousCount);
          setLocalizedDescription(endCount);
        }
        let measure;
        let count: IAuditLogItem;
        const previousMeasureStr = isEmpty(previousCount)
          ? ''
          : `${previousCount.measure_description}[${previousCount.factor_value}]`;
        const endMeasureStr = isEmpty(endCount)
          ? ''
          : `${endCount.measure_description}[${endCount.factor_value}]`;
        if (isEmpty(endCount)) {
          measure = {
            start: {
              description: previousCount.measure_description,
              factorValue: previousCount.factor_value,
            },
            isRemoved: true,
          };
          count = {
            start: previousCount.count,
            end: null,
          };
        } else if (
          isEmpty(previousCount) ||
          previousMeasureStr === endMeasureStr
        ) {
          measure = {
            start: {
              description: endCount.measure_description,
              factorValue: endCount.factor_value,
            },
          };
          count = {
            start: previousCount?.count || null,
            end: endCount.count,
          };
        } else {
          measure = {
            start: {
              description: previousCount.measure_description,
              factorValue: previousCount.factor_value,
            },
            end: {
              description: endCount.measure_description,
              factorValue: endCount.factor_value,
            },
          };
          count = {
            start: previousCount.count,
            end: endCount.count,
          };
        }

        const record = {
          date,
          operator: returnOperatorItem(operation, doc, this.i18nService),
          measure: this.auditTrailFormateMeasure(measure),
          count: this.auditTrailFormateCount(count),
        } as IInventoryCountItemItemViewAuditLog;

        records.push(record);
      }
    });

    if (!isEmpty(records)) {
      records[records.length - 1].isLast = true;

      records = [
        {
          date: doc.date,
          operator: returnOperatorItem(operation, doc, this.i18nService),
          isNotLeaf: true,
        },
        ...records,
      ];
    }

    return records;
  }

  public auditTrailFormateMeasure(
    measure: IInventoryCountMeasureAuditLogItem,
  ): string {
    if (isNil(get(measure, 'start')) && isNil(get(measure, 'end'))) {
      return '';
    }

    const formateNumber = (path: string) => {
      let startEndNumber = get(measure, path, null);
      if (!isNil(startEndNumber)) {
        startEndNumber = this.numberFormatService.getNumberFormatter(false)({
          value: startEndNumber,
        });
      } else {
        startEndNumber = '';
      }

      return startEndNumber;
    };

    if (isEmpty(measure.end)) {
      return `${measure.start.description}[${formateNumber('start.factorValue')}]${measure.isRemoved ? ` (${this.i18nService.instant(I18n.Common.Removed)})` : ''}`;
    }

    return `${measure.start.description}[${formateNumber('start.factorValue')}] → ${measure.end.description}[${formateNumber('end.factorValue')}]`;
  }

  public auditTrailFormateCount(count: IAuditLogItem): string {
    const startEnd = this.generateStartAndEndValue(count);

    return this.formateNumberForStartEnd(startEnd, { precision: 3 });
  }

  public generateStartAndEndValue(startEnd: IAuditLogItem): IAuditLogItem {
    let startValue = get(startEnd, `start`, null);
    if (!isNull(startValue) && get(startValue, '$numberDecimal')) {
      startValue = startValue.$numberDecimal.toString();
    }
    let endValue = get(startEnd, `end`, null);
    if (!isNull(endValue) && get(endValue, '$numberDecimal')) {
      endValue = endValue.$numberDecimal.toString();
    }

    return {
      start: startValue,
      end: endValue,
    };
  }

  /**
   * Fetch physical count measure config from XVI backend.
   *
   * @returns Observable with config data containing is_company_level property
   */
  public fetchPhysicalCountMeasureConfig(): Observable<{
    is_company_level: boolean;
  }> {
    return this.http
      .get<{
        is_company_level: boolean;
      }>(`${environment.xviBackendURI}/physical-count-measure/config`)
      .pipe(this.catchHttpError());
  }
}
